import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  ChevronsUpDown,
  LogOut,
  Search,
  User2,
  UserRoundX,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { authClient } from "@/lib/authClient";
import { trpc } from "@/lib/trpcClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";

type Props = {
  className?: string;
  isSideBySideMode?: boolean;
  isMobile?: boolean;
};

export function AccountDropdown({
  className,
  isSideBySideMode = true,
  isMobile,
}: Props) {
  const posthog = usePostHog();

  const { data: session } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const { name, email } = session.user!;
  const isImpersonating = !!session.session!.impersonatedBy;
  const isSuperAdmin = session.user?.role === "admin";

  const { data: orgs } = useSuspenseQuery(trpc.org.list.queryOptions());
  const [searchQuery, setSearchQuery] = useState("");

  const { mutate: logout } = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: (response) => {
      if (response.data) {
        posthog.reset();
        window.location.reload();
      }
    },
  });

  const { mutate } = useMutation(
    trpc.auth.activeOrg.mutationOptions({
      onSuccess: () => {
        window.location.reload();
      },
    }),
  );

  // Filter organizations based on search query
  const filteredOrgs = useMemo(() => {
    if (!orgs) return [];
    if (!searchQuery.trim()) return orgs;

    return orgs.filter(
      (org) =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.dbaName?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [orgs, searchQuery]);

  // Show search if more than 5 organizations
  const showSearch = orgs && orgs.length > 5;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        // Reset search when dropdown closes
        if (!open) {
          setSearchQuery("");
        }
      }}
    >
      <DropdownMenuTrigger
        className={twMerge(
          className,
          "flex items-center justify-center gap-4 rounded p-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
        )}
      >
        {isSideBySideMode ? (
          <>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">
                {name}{" "}
                {isImpersonating && <span className="text-destructive">*</span>}
              </span>
              <span className="truncate text-xs">{email}</span>
            </div>

            <ChevronsUpDown className="ml-auto size-4" />
          </>
        ) : (
          <User2 className="size-4" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-96 w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        side={isMobile ? "bottom" : "right"}
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{name}</span>
              <span className="truncate text-xs">{email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        {session.org && orgs && (
          <>
            <DropdownMenuSeparator />
            {showSearch && (
              <>
                <div className="px-2 py-1.5">
                  <div className="relative">
                    <Search className="-translate-y-1/2 absolute top-1/2 left-2 h-4 w-4 text-muted-foreground" />
                    <Input
                      autoFocus={false}
                      className="h-8 pl-8 text-sm"
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        // Prevent dropdown from closing on key presses
                        e.stopPropagation();
                      }}
                      onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                        // Prevent dropdown from closing when clicking input
                        e.stopPropagation();
                      }}
                      placeholder="Search organizations..."
                      value={searchQuery}
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <div className="max-h-48 overflow-y-auto">
              <DropdownMenuRadioGroup value={session.org.id}>
                {filteredOrgs.length > 0
                  ? filteredOrgs.map((org) => (
                      <DropdownMenuRadioItem
                        key={org.id}
                        onClick={async () => {
                          if (
                            isSuperAdmin &&
                            org?.member?.userId !== session?.session?.userId
                          ) {
                            await authClient.admin.impersonateUser({
                              fetchOptions: {
                                onSuccess: () => mutate({ orgId: org.id }),
                              },
                              userId: org?.member?.userId,
                            });
                          } else {
                            mutate({ orgId: org.id });
                          }
                        }}
                        value={org.id}
                      >
                        {org.dbaName || org.name}{" "}
                        {org?.member?.userId !== session?.session?.userId && (
                          <span className="text-destructive">*</span>
                        )}
                      </DropdownMenuRadioItem>
                    ))
                  : showSearch && (
                      <div className="px-2 py-1.5 text-muted-foreground text-sm">
                        No organizations found
                      </div>
                    )}
              </DropdownMenuRadioGroup>
            </div>
          </>
        )}

        <DropdownMenuSeparator />

        {isImpersonating && (
          <DropdownMenuItem
            onClick={async () => {
              await authClient.admin.stopImpersonating({
                fetchOptions: {
                  onSuccess: () => window.location.reload(),
                },
              });
            }}
          >
            <UserRoundX />
            Stop Impersonating
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={() => logout()}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
